import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { resolveDownloadUrl } from '@/api/endpoints/youtubeDownload'
import type { ProjectVideo } from '../model'
import { loadPlayerVolume, savePlayerVolume } from '../projectStore'
import {
  resolvePoint,
  timeOfSource,
  type TimelineSegment,
  type VirtualTimeline,
} from '../timeline'

/**
 * Playback over the project's virtual timeline.
 *
 * The player doesn't know about "the current video" — it knows a position
 * in project seconds and asks {@link resolvePoint} which file renders it.
 * Running off the end of one segment into the next is the *normal* case,
 * not a special preview mode: that's what makes several videos read as one
 * continuous piece, and what makes "só os cortes" look like a single video
 * rather than a playlist.
 *
 * ``bound`` is the only thing layered on top: previewing one clip is
 * playing the timeline with a stop at that clip's end.
 */

/**
 * Two video elements, swapped at segment boundaries.
 *
 * Seeking mid-playback is what made the seams stutter: the element has to
 * fetch a new byte range and flush its decoder, and the player freezes
 * meanwhile. Since the segment order is known in advance, the idle element
 * loads and seeks to the *next* segment while the visible one is still
 * playing — the boundary then costs a `play()`, not a seek.
 */
export type SlotKey = 'a' | 'b'

interface SlotState {
  videoId: string | null
  src: string | null
}

export interface PlayerSlot {
  key: SlotKey
  src: string | null
  ref: React.RefObject<HTMLVideoElement>
  active: boolean
}

/** A stop imposed on top of continuous playback — previewing one clip. */
export interface PlaybackBound {
  start: number
  end: number
  /** Clip being previewed, so the UI can show which row is playing. */
  clipId: number
}

/** What a ``<video>`` can tell us about a source that the server didn't. */
export interface VideoMetadata {
  duration: number
  /** Of whatever file this element loaded — the **proxy**, usually. Its
   * *shape* matches the master, which is the part anything downstream
   * needs; its size doesn't. */
  width: number
  height: number
}

export interface ClipPlaybackOptions {
  timeline: VirtualTimeline
  videos: ProjectVideo[]
  /** Fired when the browser reports metadata, so the project can cache it —
   * the imports endpoint doesn't probe, and without a duration the
   * concatenated timeline can't lay a video out, while without dimensions
   * the stage can't give the frame its real shape. */
  onMetadata?: (videoId: string, info: VideoMetadata) => void
}

export interface SlotHandlers {
  onLoadedMetadata: (event: React.SyntheticEvent<HTMLVideoElement>) => void
  onTimeUpdate: (event: React.SyntheticEvent<HTMLVideoElement>) => void
  onPlay: () => void
  onPause: () => void
}

export interface ClipPlaybackApi {
  playing: boolean
  /** Position on the project timeline, in project seconds. */
  playhead: number
  /** Index of the segment currently loaded — the editor derives "which
   * video am I looking at" from this. */
  segmentIndex: number
  bound: PlaybackBound | null
  volume: number
  muted: boolean
  setVolume: (value: number) => void
  toggleMute: () => void
  /** Both players, in a stable order; render them stacked and hide the
   * inactive one. */
  slots: PlayerSlot[]
  handlersFor: (slot: SlotKey) => SlotHandlers
  togglePlay: () => void
  pause: () => void
  resume: () => void
  seekTo: (time: number, options?: { keepBound?: boolean }) => void
  seekBy: (delta: number) => void
  /** Play just this range, stopping at its end. */
  playBounded: (bound: PlaybackBound) => void
  /** Play the whole timeline from ``time`` (default: the start). */
  playFrom: (time?: number) => void
  clearBound: () => void
}

/** Tolerance when comparing currentTime to a segment's end. Without it the
 * rAF loop can overshoot by a frame and clip the last moment off. Scaled by
 * the segment's rate when compared in source seconds: at 4× a frame of wall
 * clock is four frames of source. */
const END_EPSILON_S = 0.02
/** How far the preloaded element may sit from the next segment's start and
 * still count as ready. A seek lands within a frame or two of the ask. */
const PRELOAD_TOLERANCE_S = 0.5
/** ``HAVE_CURRENT_DATA``: the frame at the current position is decoded,
 * which is all the swap needs to be seamless. */
const READY_ENOUGH = 2

export function useClipPlayback({
  timeline,
  videos,
  onMetadata,
}: ClipPlaybackOptions): ClipPlaybackApi {
  const [playing, setPlaying] = useState(false)
  const [playhead, setPlayhead] = useState(0)
  const [segmentIndex, setSegmentIndex] = useState(0)
  const [bound, setBound] = useState<PlaybackBound | null>(null)
  // One read of localStorage, not one per field.
  const [stored] = useState(loadPlayerVolume)
  const [volume, setVolumeState] = useState(stored.volume)
  const [muted, setMuted] = useState(stored.muted)
  const [active, setActive] = useState<SlotKey>('a')
  const [slots, setSlots] = useState<Record<SlotKey, SlotState>>({
    a: { videoId: null, src: null },
    b: { videoId: null, src: null },
  })

  const refA = useRef<HTMLVideoElement>(null)
  const refB = useRef<HTMLVideoElement>(null)

  // Refs mirror state for the rAF loop, which must read fresh values
  // without re-subscribing every frame.
  const timelineRef = useRef(timeline)
  timelineRef.current = timeline
  // Playhead in a ref so seekBy doesn't re-create on every frame of playback.
  const playheadRef = useRef(playhead)
  playheadRef.current = playhead
  const boundRef = useRef(bound)
  boundRef.current = bound
  const segmentIndexRef = useRef(segmentIndex)
  segmentIndexRef.current = segmentIndex
  const activeRef = useRef(active)
  activeRef.current = active
  const slotsRef = useRef(slots)
  slotsRef.current = slots

  /** Seek (and optional autoplay) waiting for the *visible* element to
   * finish loading a new source. */
  const pendingActiveRef = useRef<{ time: number; play: boolean } | null>(null)
  /** Seek waiting for the *idle* element to finish preloading. */
  const pendingPreloadRef = useRef<{ slot: SlotKey; time: number } | null>(null)

  /**
   * A freeze in progress.
   *
   * While this is set the ``<video>`` is **paused on the held frame** and
   * the playhead is driven by wall clock instead of by the element — there
   * is no other way to make a still last three seconds. The consequence is
   * that ``playing`` can no longer be read off the element's own events:
   * the pause that starts a freeze must not read as "the user stopped", so
   * ``onPause`` ignores it while this is non-null.
   *
   * Audio needs no special handling in the preview: a paused element is
   * silent, which is exactly the "áudio mudo" the export enforces with a
   * silent track.
   */
  const freezeRef = useRef<{ freezeId: string; startedAt: number } | null>(null)

  const elementOf = useCallback((slot: SlotKey) => (slot === 'a' ? refA.current : refB.current), [])
  const activeElement = useCallback(() => elementOf(activeRef.current), [elementOf])
  const idleSlot = useCallback((): SlotKey => (activeRef.current === 'a' ? 'b' : 'a'), [])

  // The player reads the *proxy*, never the master. A 4K60 master keyframes
  // ~4 s apart, so every clip boundary was a seek that decoded up to 250
  // frames of 8,3 Mpixel video; the proxy is 720p with a keyframe every
  // 0,5 s. Cutting and export still read ``tempPath`` — the master is
  // untouched, this is only what you *watch* while editing.
  const srcOf = useCallback(
    (videoId: string): string | null => {
      const video = videos.find((v) => v.id === videoId)
      if (!video) return null
      return resolveDownloadUrl(video.proxyUrl ?? video.videoUrl)
    },
    [videos],
  )

  // --- Volume --------------------------------------------------------------
  // Applied to *both* elements, always. The idle one is mid-preload for the
  // next segment and becomes visible at the seam — if it kept the default
  // volume, every boundary would jump back to full blast.
  const volumeRef = useRef({ volume, muted })
  volumeRef.current = { volume, muted }

  const applyVolume = useCallback((el: HTMLVideoElement | null) => {
    if (!el) return
    el.volume = volumeRef.current.volume
    el.muted = volumeRef.current.muted
  }, [])

  useEffect(() => {
    applyVolume(refA.current)
    applyVolume(refB.current)
    savePlayerVolume({ volume, muted })
  }, [volume, muted, applyVolume])

  // --- Rate ----------------------------------------------------------------
  // Per-segment, because speed is a property of the clip the segment came
  // from. Kept in a ref for the same reason as the volume: an element that
  // has just swapped `src` never ran the effect below, and the seam would
  // jump back to 1× halfway through a re-timed clip.
  const rateRef = useRef(1)

  const applyRate = useCallback((el: HTMLVideoElement | null, rate: number) => {
    if (!el) return
    rateRef.current = rate
    // Assigning an unsupported rate throws; the model already clamps to
    // 0,25–4, which every browser accepts.
    el.playbackRate = rate
  }, [])

  const setVolume = useCallback((value: number) => {
    const clamped = Math.max(0, Math.min(1, value))
    setVolumeState(clamped)
    // Dragging to zero is the same intent as muting, and dragging back up
    // should be audible without a second click on the speaker.
    setMuted(clamped === 0)
  }, [])

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      // Unmuting a slider that sits at zero would stay silent and read as
      // a broken button.
      if (prev && volumeRef.current.volume === 0) setVolumeState(1)
      return !prev
    })
  }, [])

  // --- Source wiring -------------------------------------------------------
  // The visible slot must hold the segment the playhead is on. Editing a
  // clip or removing a video reshapes the timeline underneath, so the index
  // is clamped rather than trusted.
  const segmentCount = timeline.segments.length
  useEffect(() => {
    if (segmentCount === 0) return
    if (segmentIndexRef.current < segmentCount) return
    setSegmentIndex(segmentCount - 1)
  }, [segmentCount])

  const currentSegment = timeline.segments[Math.min(segmentIndex, segmentCount - 1)] ?? null
  const currentVideoId = currentSegment?.videoId ?? null
  const currentSpeed = currentSegment?.speed ?? 1

  useEffect(() => {
    applyRate(elementOf(activeRef.current), currentSpeed)
  }, [currentSpeed, active, applyRate, elementOf])

  useEffect(() => {
    if (!currentVideoId) return
    const slot = activeRef.current
    if (slotsRef.current[slot].videoId === currentVideoId) return
    const src = srcOf(currentVideoId)
    if (!src) return
    setSlots((prev) => ({ ...prev, [slot]: { videoId: currentVideoId, src } }))
  }, [currentVideoId, srcOf])

  // Preload the next segment into the idle element while this one plays.
  // Unlike the old sequence-only preload this is always on: crossing a
  // segment boundary is now ordinary playback.
  const nextSegment = timeline.segments[segmentIndex + 1] ?? null
  useEffect(() => {
    if (!nextSegment || !playing) return
    // A freeze needs no preload: it holds a frame of the file already
    // loaded, and the swap it would trigger costs a decoder for nothing.
    if (nextSegment.freezeId) return
    const slot = idleSlot()
    const alreadyLoaded = slotsRef.current[slot].videoId === nextSegment.videoId
    if (!alreadyLoaded) {
      const src = srcOf(nextSegment.videoId)
      if (!src) return
      setSlots((prev) => ({ ...prev, [slot]: { videoId: nextSegment.videoId, src } }))
    }
    const el = elementOf(slot)
    if (alreadyLoaded && el && el.readyState >= 1) {
      // Same file as before: seek straight away, no reload needed.
      el.currentTime = nextSegment.sourceStart
      pendingPreloadRef.current = null
    } else {
      // Applied by the element's own loadedmetadata.
      pendingPreloadRef.current = { slot, time: nextSegment.sourceStart }
    }
  }, [nextSegment, playing, srcOf, elementOf, idleSlot])

  // Paused with nothing queued → drop the spare source so a second decoder
  // isn't kept alive on a multi-gigabyte file.
  useEffect(() => {
    if (playing) return
    const slot = idleSlot()
    pendingPreloadRef.current = null
    setSlots((prev) =>
      prev[slot].src === null ? prev : { ...prev, [slot]: { videoId: null, src: null } },
    )
  }, [playing, active, idleSlot])

  // --- Transport -----------------------------------------------------------
  const clearBound = useCallback(() => {
    setBound(null)
  }, [])

  /**
   * Start (or resume) holding a frame.
   *
   * ``at`` is where on the timebase the hold should be considered to have
   * reached already, so pressing play in the middle of a three-second
   * freeze finishes the remaining time instead of restarting it.
   */
  const startFreeze = useCallback(
    (segment: TimelineSegment, at: number) => {
      if (!segment.freezeId) return
      const el = elementOf(activeRef.current)
      // Order matters: the flag has to be up before the pause, or the
      // element's own ``onPause`` reads this as the user stopping.
      freezeRef.current = {
        freezeId: segment.freezeId,
        startedAt: performance.now() - Math.max(0, at - segment.start) * 1000,
      }
      if (el) {
        el.pause()
        el.currentTime = segment.sourceStart
      }
      setPlaying(true)
    },
    [elementOf],
  )

  /** Move to a project-second, loading another file if that's where it
   * lives. ``play`` continues (or starts) playback once it lands. */
  const goTo = useCallback(
    (time: number, play: boolean) => {
      const point = resolvePoint(timelineRef.current, time)
      if (!point) return
      const clamped = Math.max(0, Math.min(timelineRef.current.duration, time))
      setPlayhead(clamped)
      setSegmentIndex(point.index)
      segmentIndexRef.current = point.index
      // Any deliberate jump ends whatever hold was running; landing back on
      // one is re-established below with the right elapsed time.
      freezeRef.current = null

      const slot = activeRef.current
      const el = elementOf(slot)
      if (slotsRef.current[slot].videoId === point.segment.videoId && el) {
        applyRate(el, point.segment.speed)
        el.currentTime = point.sourceTime
        if (!play) return
        if (point.segment.freezeId) startFreeze(point.segment, clamped)
        else void el.play()
        return
      }
      // Different file: the seek has to wait for loadedmetadata.
      const src = srcOf(point.segment.videoId)
      if (!src) return
      pendingActiveRef.current = { time: point.sourceTime, play }
      setSlots((prev) => ({ ...prev, [slot]: { videoId: point.segment.videoId, src } }))
    },
    [elementOf, srcOf, applyRate, startFreeze],
  )

  const seekTo = useCallback(
    (time: number, options?: { keepBound?: boolean }) => {
      // A manual jump means the user took over — a clip preview that kept
      // stopping them at some unrelated boundary would feel broken.
      if (!options?.keepBound) setBound(null)
      goTo(time, false)
    },
    [goTo],
  )

  const seekBy = useCallback(
    (delta: number) => {
      seekTo(playheadRef.current + delta)
    },
    [seekTo],
  )

  /** The segment the playhead is on, read fresh — every transport handler
   * needs it and none of them may depend on a value that changes per frame. */
  const currentSegmentRef = useCallback(
    () => timelineRef.current.segments[segmentIndexRef.current] ?? null,
    [],
  )

  const togglePlay = useCallback(() => {
    const el = activeElement()
    if (!el) return
    // Mid-hold the element is paused, so "is it playing?" can't be read off
    // it — the freeze flag is the answer.
    if (freezeRef.current) {
      freezeRef.current = null
      setPlaying(false)
      return
    }
    if (!el.paused) {
      el.pause()
      return
    }
    const segment = currentSegmentRef()
    if (segment?.freezeId) startFreeze(segment, playheadRef.current)
    else void el.play()
  }, [activeElement, currentSegmentRef, startFreeze])

  const pause = useCallback(() => {
    if (freezeRef.current) {
      freezeRef.current = null
      setPlaying(false)
    }
    activeElement()?.pause()
  }, [activeElement])

  const resume = useCallback(() => {
    const segment = currentSegmentRef()
    if (segment?.freezeId) {
      startFreeze(segment, playheadRef.current)
      return
    }
    void activeElement()?.play()
  }, [activeElement, currentSegmentRef, startFreeze])

  const playBounded = useCallback(
    (next: PlaybackBound) => {
      setBound(next)
      goTo(next.start, true)
    },
    [goTo],
  )

  const playFrom = useCallback(
    (time = 0) => {
      setBound(null)
      goTo(time, true)
    },
    [goTo],
  )

  // --- Boundary watcher ----------------------------------------------------
  // ``timeupdate`` only fires ~4×/s, which would overshoot a boundary by up
  // to 250 ms — visible, and worse at a seam between two files. rAF gives
  // frame-accurate stops, and only runs while something is playing.
  useEffect(() => {
    if (!playing) return
    let raf = 0
    const tick = () => {
      raf = requestAnimationFrame(tick)
      const el = activeElement()
      if (!el) return

      const tl = timelineRef.current
      const index = segmentIndexRef.current
      const segment = tl.segments[index]
      if (!segment) return

      // --- Where the playhead is ------------------------------------------
      // Two clocks now. Footage reports its own position; a held frame has
      // none — the element is parked — so its progress is wall clock.
      let projectTime: number
      if (segment.freezeId) {
        if (!freezeRef.current) {
          // Scrubbed into a hold while running: adopt it, rather than let
          // the element play straight through the frame it should stop on.
          if (el.paused) return
          startFreeze(segment, Math.max(segment.start, playheadRef.current))
        }
        const started = freezeRef.current?.startedAt ?? performance.now()
        projectTime = segment.start + (performance.now() - started) / 1000
        setPlayhead(Math.min(segment.end, projectTime))
      } else {
        freezeRef.current = null
        if (el.paused) return
        const at = timeOfSource(tl, index, el.currentTime)
        if (at === null) return
        projectTime = at
      }

      // A bound (single-clip preview) stops before any segment logic.
      const currentBound = boundRef.current
      if (currentBound && projectTime + END_EPSILON_S >= currentBound.end) {
        freezeRef.current = null
        el.pause()
        setPlaying(false)
        setBound(null)
        return
      }

      // The tolerance is in *wall* seconds, so comparing it against source
      // time has to scale by the rate — at 4× a frame is four times as much
      // source, and an unscaled epsilon overshoots the seam.
      const finished = segment.freezeId
        ? projectTime + END_EPSILON_S >= segment.end
        : el.currentTime + END_EPSILON_S * segment.speed >= segment.sourceEnd
      if (!finished) return

      const nextIndex = index + 1
      const next = tl.segments[nextIndex]
      if (!next) {
        freezeRef.current = null
        el.pause()
        setPlaying(false)
        return
      }

      // Into a hold: same file, already decoded, so this is just "stop on
      // this frame and start counting". No preload, no swap.
      if (next.freezeId && slotsRef.current[activeRef.current].videoId === next.videoId) {
        segmentIndexRef.current = nextIndex
        setSegmentIndex(nextIndex)
        setPlayhead(next.start)
        startFreeze(next, next.start)
        return
      }

      // Fast path: the idle element is already sitting on the next
      // segment's first frame, so the seam costs a play() and nothing else.
      const spare = idleSlot()
      const spareEl = elementOf(spare)
      const spareReady =
        !next.freezeId &&
        spareEl !== null &&
        slotsRef.current[spare].videoId === next.videoId &&
        spareEl.readyState >= READY_ENOUGH &&
        Math.abs(spareEl.currentTime - next.sourceStart) < PRELOAD_TOLERANCE_S

      segmentIndexRef.current = nextIndex
      setSegmentIndex(nextIndex)

      if (spareReady) {
        el.pause()
        activeRef.current = spare
        setActive(spare)
        setPlayhead(next.start)
        applyRate(spareEl, next.speed)
        void spareEl.play()
        return
      }

      // Slow path: the preload didn't land in time.
      if (slotsRef.current[activeRef.current].videoId === next.videoId) {
        applyRate(el, next.speed)
        el.currentTime = next.sourceStart
        setPlayhead(next.start)
        if (next.freezeId) startFreeze(next, next.start)
      } else {
        el.pause()
        pendingActiveRef.current = { time: next.sourceStart, play: true }
        const src = srcOf(next.videoId)
        if (src) {
          setSlots((prev) => ({
            ...prev,
            [activeRef.current]: { videoId: next.videoId, src },
          }))
        }
      }
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
    }
  }, [playing, activeElement, elementOf, idleSlot, srcOf, applyRate, startFreeze])

  // --- <video> handlers ----------------------------------------------------
  const handlersFor = useCallback(
    (slot: SlotKey): SlotHandlers => ({
      onLoadedMetadata: (event) => {
        const el = event.currentTarget
        // A slot that just got a new ``src`` is a fresh element as far as
        // the volume and rate effects are concerned — neither ran for it.
        applyVolume(el)

        const preload = pendingPreloadRef.current
        if (preload && preload.slot === slot) {
          el.currentTime = preload.time
          pendingPreloadRef.current = null
        }

        const d = el.duration
        if (isFinite(d) && d > 0) {
          const videoId = slotsRef.current[slot].videoId
          if (videoId) {
            onMetadata?.(videoId, { duration: d, width: el.videoWidth, height: el.videoHeight })
          }
        }

        if (slot !== activeRef.current) return
        const pending = pendingActiveRef.current
        if (pending) {
          pendingActiveRef.current = null
          const segment = timelineRef.current.segments[segmentIndexRef.current] ?? null
          applyRate(el, segment?.speed ?? 1)
          el.currentTime = pending.time
          if (!pending.play) return
          // The awaited seek can land on a hold — a freeze that opens a
          // clip whose footage lives in a different file.
          if (segment?.freezeId) startFreeze(segment, segment.start)
          else void el.play()
        }
      },
      onTimeUpdate: (event) => {
        // The idle element seeks while preloading; its timeupdate must not
        // drag the playhead around.
        if (slot !== activeRef.current) return
        // A held frame maps a whole span of project time onto **one** source
        // instant, so the element's clock cannot say where the playhead is —
        // asking it always answers "the start of the freeze". Honouring that
        // is what made clicking into the middle of a hold jump to its
        // beginning, and with it went any chance of editing a caption that
        // lives inside one.
        if (freezeRef.current) return
        if (timelineRef.current.segments[segmentIndexRef.current]?.freezeId) return
        const projectTime = timeOfSource(
          timelineRef.current,
          segmentIndexRef.current,
          event.currentTarget.currentTime,
        )
        if (projectTime !== null) setPlayhead(projectTime)
      },
      onPlay: () => {
        if (slot !== activeRef.current) return
        setPlaying(true)
      },
      onPause: () => {
        if (slot !== activeRef.current) return
        // Holding a frame *is* playing, as far as the transport is
        // concerned — the pause that starts one must not flip the button.
        if (freezeRef.current) return
        setPlaying(false)
      },
    }),
    [onMetadata, applyVolume, applyRate, startFreeze],
  )

  const playerSlots = useMemo<PlayerSlot[]>(
    () => [
      { key: 'a', src: slots.a.src, ref: refA, active: active === 'a' },
      { key: 'b', src: slots.b.src, ref: refB, active: active === 'b' },
    ],
    [slots, active],
  )

  return {
    playing,
    playhead,
    segmentIndex,
    bound,
    volume,
    muted,
    setVolume,
    toggleMute,
    slots: playerSlots,
    handlersFor,
    togglePlay,
    pause,
    resume,
    seekTo,
    seekBy,
    playBounded,
    playFrom,
    clearBound,
  }
}
